import React from 'react'
import { CloudBillingPlanFragment } from '@sketch/gql-types'

import PlanPickerCard from '../PlanPickerCard'
import {
  PlanPickerCardWrapper,
  PlansWrapper,
  SmallText,
} from './PlanPicker.styles'

// This is a valid use case to narrow down Component type
// eslint-disable-next-line no-restricted-imports
import { Currency } from '@sketch/gql-types/expansive'
import { Tooltip } from '@sketch/components'

const PLAN_PRODUCT_TO_TITLE = {
  BUSINESS: 'Business',
  ESSENTIAL: 'Essential',
  STUDENT: 'Student',
  TEAMS: 'Standard',
}

interface PlansProps {
  isDisabled?: boolean
  currency: Currency
  selectedPlan?: CloudBillingPlanFragment
  onlyBusinessPlansAllowed: boolean
  plans: CloudBillingPlanFragment[]
  onChange: (clickedPlan: CloudBillingPlanFragment) => void
}

/**
 * PlanPicker
 *
 * Renders cards with each sketch plan (Trial, Monthly, Yearly) detail
 * and allows the user to select one of them.
 *
 * Trial can be hidden for certain users using the showTrial flag
 *
 * @example
 * <PlanPicker onChange={(clickedPlan) => {...}} />
 *
 */
export const PlanPicker: React.FC<PlansProps> = ({
  currency,
  isDisabled = false,
  onChange,
  selectedPlan,
  onlyBusinessPlansAllowed,
  plans,
}) => {
  const formatPlanContent = (plan: CloudBillingPlanFragment) => ({
    title: PLAN_PRODUCT_TO_TITLE[plan.product],
    description: 'Monthly / per Editor',
    price: plan.prices.find(price => price.currency === currency)?.monthly || 0,
    currency: currency,
    testId: plan.id,
  })

  const content = plans.map(plan => {
    const card = (
      <PlanPickerCard
        onChange={onChange}
        plan={plan}
        formatContent={formatPlanContent}
        isSelected={plan.id === selectedPlan?.id}
        isDisabled={
          isDisabled ||
          (selectedPlan?.type === 'MONTHLY' && plan.product === 'BUSINESS') ||
          (onlyBusinessPlansAllowed && plan.product !== 'BUSINESS')
        }
        currency={currency}
      />
    )

    if (plan.product === 'BUSINESS') {
      return (
        <Tooltip
          key={plan.id}
          placement="top"
          content="Available on yearly billing only"
          disabled={
            !(
              (selectedPlan?.type === 'MONTHLY' &&
                plan.product === 'BUSINESS') ||
              (onlyBusinessPlansAllowed && plan.product !== 'BUSINESS')
            )
          }
        >
          {({ ref, ...otherProps }) => (
            <PlanPickerCardWrapper ref={ref} {...otherProps}>
              {card}
              <SmallText>Available on yearly billing only</SmallText>
            </PlanPickerCardWrapper>
          )}
        </Tooltip>
      )
    }

    return <PlanPickerCardWrapper key={plan.id}>{card}</PlanPickerCardWrapper>
  })

  return <PlansWrapper>{content}</PlansWrapper>
}
